import { trackAnalyticsEvent } from '@autone/analytics';
import { useAuthorization } from '@autone/auth';
import { useAutoneTranslation } from '@autone/translations';
import {
  AutoneTooltip,
  createColumnLoadingGuards,
  DataTableCellContent,
  DataTableCellTitle,
  type DataTableLoadingObject,
} from '@autone/ui';
import { NumberInput } from '@autone/ui-next/atoms';
import { type ColumnHelper } from '@tanstack/react-table';
import { Info } from 'lucide-react';
import { useCallback, useState } from 'react';

import { type APP_NAME } from '../../../../consts';
import {
  useEditAssortmentSkuLocationMutation,
  useGetAssortmentCustomerConfigQuery,
} from '../../../../redux/assortment';
import { type TransformedAssortmentSkuLocationsItem } from '../../../../redux/assortment/schemas/get-assortment-sku-locations.schema';

const COLUMN_ID = 'initial_allocation' as const;

export type InitialAllocationColumnId = typeof COLUMN_ID;

interface InitialAllocationFields {
  sku: TransformedAssortmentSkuLocationsItem['sku'];
  location: TransformedAssortmentSkuLocationsItem['location'];
  initial_allocation: TransformedAssortmentSkuLocationsItem['initial_allocation'];
  is_assorted: TransformedAssortmentSkuLocationsItem['is_assorted'];
}

const { columnCellGuard, accessorFnGuard } =
  createColumnLoadingGuards<InitialAllocationFields>();

export const createInitialAllocationColumn = <
  T extends InitialAllocationFields | DataTableLoadingObject,
>(
  columnHelper: ColumnHelper<T>,
) =>
  columnHelper.accessor(
    accessorFnGuard((row) => row.initial_allocation),
    {
      id: COLUMN_ID,
      size: 150,
      header: function InitialAllocationHeader() {
        const { t } = useAutoneTranslation<typeof APP_NAME>();

        return (
          <DataTableCellContent align={'right'}>
            <DataTableCellTitle className="flex flex-row gap-2 items-center uppercase font-medium whitespace-normal text-right">
              {t('table-columns.initial-allocation')}
              <AutoneTooltip
                text={t(
                  'table-column-tooltips.initial-allocation.product-locations-sku-table',
                )}
              >
                <Info className="inline" size={14} />
              </AutoneTooltip>
            </DataTableCellTitle>
          </DataTableCellContent>
        );
      },
      cell: (ctx) =>
        columnCellGuard({
          ctx,
          renderCell: function InitialAllocationCell({ row }) {
            const { initial_allocation, sku, location } = row.original;
            const { hasReadOnlyAccess } = useAuthorization();
            const [value, setValue] = useState(initial_allocation.value);

            const [editSkuLocationTrigger] =
              useEditAssortmentSkuLocationMutation();
            const { data, isLoading: configIsLoading } =
              useGetAssortmentCustomerConfigQuery({});
            const { use_data_feed_for_initial_allocation } = data ?? {};

            const handleEditInitialAllocation = useCallback(() => {
              if (value === initial_allocation.value) return;

              trackAnalyticsEvent({
                category: 'assortment_tables',
                action: 'sku_locations_table:initial_allocation_updated',
                properties: {
                  sku_id: sku.id,
                  location_id: location.id,
                  value,
                },
              });
              editSkuLocationTrigger({
                path: { sku_id: sku.id, location_id: location.id },
                body: {
                  assortment_update: {
                    update_type: 'initial_allocation',
                    new_value: value ?? 0,
                  },
                },
              });
            }, [
              sku.id,
              location.id,
              initial_allocation.value,
              value,
              editSkuLocationTrigger,
            ]);

            const isDisabled =
              initial_allocation.awaiting_requests > 0 ||
              initial_allocation.is_disabled ||
              hasReadOnlyAccess ||
              configIsLoading ||
              use_data_feed_for_initial_allocation === true ||
              use_data_feed_for_initial_allocation === undefined;

            return (
              <DataTableCellContent align={'right'}>
                <NumberInput
                  id={`sku-locations-initial-allocation-input-${sku.id}-${location.id}`}
                  min={0}
                  max={9999}
                  step={1}
                  variant={'small'}
                  onBlur={handleEditInitialAllocation}
                  onKeyDown={(e) =>
                    e.key === 'Enter' && handleEditInitialAllocation()
                  }
                  disabled={isDisabled}
                  value={value}
                  onChange={setValue}
                />
              </DataTableCellContent>
            );
          },
        }),
    },
  );
